import gsap from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * @description 慣性スクロール その他スクロールに関する機能を提供
 * @requires gsap - GSAPの読み込みが必要
 * @requires gsap/ScrollTrigger - GSAPのScrollTriggerプラグイン必須
 * @requires gsap/ScrollToPlugin - GSAPのScrollToPluginプラグイン必須
 * @param {String} target - 慣性スクロールを生成する親要素
 * @param {String} viewportId - 慣性スクロール表示範囲のID
 * @param {String} containerId - 慣性スクロール全体のID
 * @param {Number} scrub - スクラブの値
 * @param {Number} breakpoints - ブレークポイント
 * @param {Object|Boolean} smooth - smooth オプション。オブジェクト形式または bool型 オブジェクト形式の場合: { offsetY: number|string }
 * @property {Number|String} offsetY - スムーススクロールの垂直方向のオフセット。
 * @param {Object|Boolean} anchor - anchor オプション。オブジェクト形式または bool型 オブジェクト形式の場合: { offsetY: number|string }
 * @property {Number|String} offsetY - アンカーリンクの垂直方向のオフセット。
 * @param {Boolean} pin - false の場合: data-pin を無効にする。
 * @param {Boolean} parallax - false の場合: data-parallax を無効にする。
 */
export default class RelaxScroll {
	constructor(obj = {}) {
		this.obj = {
			breakpoints: 768,
			inertia: true,
			smooth: true,
			anchor: true,
			pin: true,
			parallax: true,
			...obj,
		};
		this.breakpoints = this.obj.breakpoints;
		this.init();
	}

	/**
	 * 初期化
	 * @method init
	 */
	init() {
		const windowSize =
			document.documentElement.clientWidth ?? window.innerWidth;
		this.cleatArea = new CleatArea(this.obj);
		if (windowSize > this.breakpoints) {
			if (this.obj.inertia) {
				this.inertiaScroll = new InertiaScroll(this.obj);
			}
		}
		if (this.obj.smooth) {
			this.smoothScroll = new SmoothScroll(this.obj.smooth.offsetY);
		}
		if (this.obj.anchor) {
			this.anchorLink = new AnchorLink(this.obj.anchor.offsetY);
		}
		if (this.obj.pin) {
			this.pin = new ScrollPin(this.breakpoints);
		}
		if (this.obj.paralla) {
			this.parallax = new ScrollParallax();
		}
	}
}

/**
 * エリアの生成機能を提供するクラス
 * @class CleatArea
 * @param {Object} obj - オプションオブジェクト
 * @param {String} obj.viewportId - 慣性スクロール表示範囲のID
 * @param {String} obj.containerId - 慣性スクロール全体のID
 * @param {String} [obj.target='.loading_wrapper'] - コンテンツを含む親要素のセレクタ
 */
class CleatArea {
	/**
	 * @constructor
	 * @param {Object} obj - オプションオブジェクト
	 */
	constructor(obj) {
		this.obj = obj;
		this.target = this.obj.target ?? '.loading-wrapper';
		this.viewportId = this.obj.viewportId ?? 'relax-viewport';
		this.containerId = this.obj.containerId ?? 'relax-container';
		this.init();
		this.setupGSAP();
	}

	/**
	 * 初期化
	 * @method init
	 */
	init() {
		const viewport = document.createElement('div');
		viewport.id = this.viewportId;
		const container = document.createElement('div');
		container.id = this.containerId;
		const wrapper = document.querySelector(this.target);
		const wrapperNodes = Array.from(wrapper.childNodes);
		wrapper.insertBefore(viewport, wrapper.firstChild);
		wrapperNodes.forEach((child) => {
			container.appendChild(child);
		});
		viewport.appendChild(container);
		if (window.innerWidth > this.obj.breakpoints) {
			ScrollSmootherBar.init();
		}
	}

	/**
	 * GSAPのセットアップ
	 * @method setupGSAP
	 */
	setupGSAP() {
		gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
	}
}

/**
 * 慣性スクロール機能を提供するクラス
 * @class InertiaScroll
 * @param {Object} obj - オプションオブジェクト
 * @param {String} obj.viewportId - 慣性スクロール表示範囲のID
 * @param {String} obj.containerId - 慣性スクロール全体のID
 * @param {Number} obj.scrub - スクラブの値
 */
class InertiaScroll {
	/**
	 * @constructor
	 * @param {Object} obj - オプションオブジェクト
	 */
	constructor(obj) {
		this.obj = obj;
		this.scrub = this.obj.scrub ?? 1;
		this.smoothView = document.getElementById(
			this.obj.viewportId ?? 'relax-viewport',
		);
		this.smoothCont = document.getElementById(
			this.obj.containerId ?? 'relax-container',
		);
		if (this.smoothView && this.smoothCont) {
			this.init();
		}
	}

	/**
	 * 初期化
	 * @method init
	 */
	init() {
		gsap.set(this.smoothView, {
			overflow: 'hidden',
			position: 'fixed',
			height: '100%',
			width: '100%',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
		});
		gsap.set(this.smoothCont, {
			overflowX: 'visible',
			width: '100%',
			zIndex: 10,
		});

		const getProp = gsap.getProperty(this.smoothCont);
		const setProp = gsap.quickSetter(this.smoothCont, 'y', 'px');
		const setScroll = ScrollTrigger.getScrollFunc(window);
		let isProxyScrolling;

		ScrollTrigger.addEventListener('refreshInit', () => this.setHeight());
		ScrollTrigger.addEventListener('resize', () => this.setHeight());
		ScrollTrigger.defaults({ scroller: this.smoothCont });
		ScrollTrigger.prototype.update = (p) => p;
		ScrollTrigger.scrollerProxy(this.smoothCont, {
			scrollTop(...args) {
				if (args.length) {
					const value = args[0];
					isProxyScrolling = true;
					setProp(-value);
					setScroll(value);
					return;
				}
				return -getProp('y');
			},
			scrollHeight: () => document.body.scrollHeight,
			getBoundingClientRect() {
				return {
					top: 0,
					left: 0,
					width: window.innerWidth,
					height: window.innerHeight,
				};
			},
		});

		const animation = gsap.to(this.smoothCont, {
			y: () => {
				this.resizeObserver.observe(this.smoothCont);
				return document.documentElement.clientHeight - this.setHeight();
			},
			ease: 'none',
			onUpdate: ScrollTrigger.update,
		});

		ScrollTrigger.create({
			animation: animation,
			scroller: window,
			invalidateOnRefresh: true,
			start: 'top top',
			end: 'bottom bottom',
			refreshPriority: -999,
			scrub: this.scrub,
			onUpdate: (self) => {
				ScrollSmootherBar.setHandler(self.progress, this.scrub);
				if (isProxyScrolling) {
					this.killScrub(self);
					isProxyScrolling = false;
				}
			},
			onRefresh: this.killScrub,
		});
	}

	/**
	 * コンテナの高さを設定
	 * @method setHeight
	 * @returns {Number} - 設定された高さ
	 */
	setHeight() {
		const height = this.smoothCont.clientHeight;
		document.body.style.height = `${height}px`;
		return height;
	}

	/**
	 * リサイズオブザーバー
	 * @type {ResizeObserver}
	 */
	resizeObserver = new ResizeObserver(() => {
		this.setHeight();
		ScrollTrigger.refresh();
		return;
	});

	/**
	 * スクラブを停止する
	 * @method killScrub
	 * @param {Object} trigger - ScrollTriggerオブジェクト
	 */
	killScrub(trigger) {
		const scrub = trigger.getTween
			? trigger.getTween()
			: gsap.getTweensOf(trigger.animation)[0];
		scrub?.pause();
		trigger.animation.progress(trigger.progress);
	}

	/**
	 * ScrollTriggerを更新
	 * @method updateScrollTrigger
	 */
	updateScrollTrigger() {
		ScrollTrigger.update();
	}
}

/**
 * スクロールバー機能を提供する静的クラス
 * @class ScrollSmootherBar
 */
class ScrollSmootherBar {
	/**
	 * 初期化メソッド
	 * @method init
	 */
	static init() {
		const handler = document.createElement('div');
		handler.id = 'relax-scrollbar';
		gsap.set(handler, {
			width: 8,
			background: 'rgba(0, 0, 0, 0.35)',
			borderRadius: '4px',
			position: 'fixed',
			right: 2,
			zIndex: 999,
			cursor: 'grab',
			backdropFilter: 'blur(10px)',
		});
		document.body.appendChild(handler);
		ScrollSmootherBar.setHandler(0);
		ScrollSmootherBar.addMouseEvents(handler);
		ScrollSmootherBar.monitorScroll();
	}

	/**
	 * スクロールバーのハンドラを設定するメソッド
	 * @method setHandler
	 * @param {number} progress - スクロールの進行状況
	 * @param {number} [scrub] - スクラブの値
	 */
	static setHandler(progress, scrub = 0) {
		const contentHeight = document.body.scrollHeight;
		const handler = document.querySelector('#relax-scrollbar');
		const handleHeight =
			(window.innerHeight / contentHeight) * window.innerHeight;
		gsap.set(handler, { height: handleHeight });
		gsap.to(handler, {
			opacity: 1,
			top: progress * (window.innerHeight - handleHeight),
			duration: scrub / 2,
		});
	}

	/**
	 * マウスイベントを追加するメソッド
	 * @method addMouseEvents
	 * @param {HTMLElement} trigger - イベントを追加する要素
	 */
	static addMouseEvents(trigger) {
		trigger.addEventListener('mousedown', (e) => {
			e.preventDefault();
			const startY = e.clientY;
			const startTop = trigger.offsetTop;
			const maxHeight = window.innerHeight - trigger.offsetHeight;

			/**
			 * マウス移動時の処理
			 * @method onMouseMove
			 */
			const onMouseMove = (e) => {
				const deltaY = e.clientY - startY;
				const newTop = Math.min(Math.max(startTop + deltaY, 0), maxHeight);
				const progress = newTop / maxHeight;
				window.scrollTo(
					0,
					progress * (document.body.scrollHeight - window.innerHeight),
				);
			};

			/**
			 * マウスアップ時の処理
			 * @method onMouseUp
			 */
			const onMouseUp = () => {
				document.removeEventListener('mousemove', onMouseMove);
				document.removeEventListener('mouseup', onMouseUp);
				gsap.to(trigger, { opacity: 0.5, delay: 0.5, duration: 0.5 });
			};

			document.addEventListener('mousemove', onMouseMove);
			document.addEventListener('mouseup', onMouseUp);
		});

		trigger.addEventListener('pointerdown', () => {
			gsap.to(trigger, { opacity: 1, duration: 0.5 });
		});
	}

	/**
	 * スクロールを監視するメソッド
	 * @method monitorScroll
	 */
	static monitorScroll() {
		let isScrolling;
		window.addEventListener('scroll', () => {
			const handler = document.querySelector('#relax-scrollbar');
			gsap.to(handler, { opacity: 1, duration: 0.5 });
			window.clearTimeout(isScrolling);
			isScrolling = setTimeout(() => {
				gsap.to(handler, { opacity: 0.5, delay: 0.5, duration: 0.5 });
			}, 200);
		});
	}
}

/**
 * ターゲット位置までスクロールする機能を提供するクラス
 * @class ScrollToTarget
 */
class ScrollToTarget {
	/**
	 * ターゲット位置までスクロールします
	 * @static
	 * @method scrollTo
	 * @param {Element|String} target - スクロールするターゲット要素またはセレクター
	 * @param {Number} offsetY - スクロールオフセット
	 */
	static scrollTo(target, offsetY) {
		gsap.to(window, {
			ease: 'Power2.easeInOut',
			duration: 0.7,
			scrollTo: {
				y: target,
				offsetY: offsetY,
				autoKill: false,
			},
		});
	}

	/**
	 * オフセットの高さを取得します
	 * @static
	 * @method getOffsetY
	 * @param {String} offsetY - オフセットセレクター
	 * @return {Number} オフセットの高さ
	 */
	static getOffsetY(offsetY) {
		if (!offsetY) {
			return 0;
		}
		const element = document.querySelector(offsetY);
		return element ? element.offsetHeight : 0;
	}
}

/**
 * スムーススクロール機能を提供するクラス
 * @class SmoothScroll
 */
class SmoothScroll {
	/**
	 * @constructor
	 * @param {String} offsetY - オフセットセレクター
	 */
	constructor(offsetY) {
		/**
		 * オフセットの高さ
		 * @type {Number}
		 */
		this.offsetY = ScrollToTarget.getOffsetY(offsetY);

		/**
		 * スクロールリンクのリスト
		 * @type {NodeListOf<Element>}
		 */
		this.hasLinks = document.querySelectorAll('a[href*="#"]');

		if (this.hasLinks.length > 0) {
			this.init();
		}
	}

	/**
	 * 初期化
	 * @method init
	 */
	init() {
		this.hasLinks.forEach((hasLink) => {
			hasLink.addEventListener('click', (e) => {
				this.handleLinkClick(e, hasLink);
			});
		});
	}

	/**
	 * リンククリックイベントを処理します
	 * @method handleLinkClick
	 * @param {Event} event - イベントオブジェクト
	 * @param {Element} link - クリックされたリンク要素
	 */
	handleLinkClick(event, link) {
		const href = link.getAttribute('href');
		if (href && href.indexOf('#') >= 0) {
			const targetId = href.split('#')[1];
			const target = targetId
				? document.getElementById(targetId)
				: document.querySelector('body');
			if (target) {
				event.preventDefault();
				ScrollToTarget.scrollTo(target, this.offsetY);
			}
		}
	}
}

/**
 * アンカーリンク機能を提供するクラス
 * @class AnchorLink
 */
class AnchorLink {
	/**
	 * @constructor
	 * @param {String} offsetY - オフセットセレクター
	 */
	constructor(offsetY) {
		/**
		 * オフセットの高さ
		 * @type {Number}
		 */
		this.offsetY = ScrollToTarget.getOffsetY(offsetY);
		this.init();
	}

	/**
	 * 初期化
	 * @method init
	 */
	init() {
		setTimeout(() => {
			window.scrollTo(0, 0);
			this.scrollToAnchor();
		}, 10);
	}

	/**
	 * アンカーリンクへスクロールします
	 * @method scrollToAnchor
	 */
	scrollToAnchor() {
		const url = location.href;
		const formPreview = document.querySelector('.mw_wp_form_preview');
		const formComplete = document.querySelector('.mw_wp_form_complete');
		let target = null;

		if (url.includes('#')) {
			const anchor = url.split('#');
			const hash = anchor[anchor.length - 1].split('?')[0];
			target = document.getElementById(hash);
		} else if (formPreview) {
			target = formPreview;
		} else if (formComplete) {
			target = formComplete;
		} else if (!formPreview && !formComplete) {
			setTimeout(() => {
				const valThrough = document.querySelector('.val-through');
				target = valThrough ? document.querySelector('form') : null;
				target && ScrollToTarget.scrollTo(target, this.offsetY);
			}, 500);
			return;
		}

		target && ScrollToTarget.scrollTo(target, this.offsetY);
	}
}

/**
 * パララックス効果を提供するクラス
 * @class ScrollParallax
 */
class ScrollParallax {
	/**
	 * @constructor
	 */
	constructor() {
		/**
		 * パララックス要素のリスト
		 * @type {Array<Element>}
		 */
		this.parallaxElements = gsap.utils.toArray('[data-parallax]');
		if (this.parallaxElements.length > 0) {
			this.init();
		}
	}

	/**
	 * 初期化
	 * @method init
	 */
	init() {
		this.parallaxElements.forEach((element) => {
			const parallaxValue = element.dataset.parallax;
			const [startY, endY, triggerSelector] = parallaxValue
				.split(',')
				.map((value) => {
					const num = Number.parseFloat(value.trim());
					return Number.isNaN(num) ? value.trim() : num;
				});
			const finalEndY = Number.isNaN(endY) ? -startY : endY;
			const trigger = triggerSelector
				? element.closest(triggerSelector)
				: this.findClosestArticleOrSection(element);
			this.cleatParallax(element, startY, finalEndY, trigger);
		});
	}

	/**
	 * 要素に最も近いARTICLEまたはSECTION要素を見つける
	 * @method findClosestArticleOrSection
	 * @param {Element} element - 検索する要素
	 * @returns {Element|null} - 見つかった要素、またはnull
	 */
	findClosestArticleOrSection(element) {
		let current = element.parentElement;
		while (current) {
			if (current.tagName === 'ARTICLE' || current.tagName === 'SECTION') {
				return current;
			}
			current = current.parentElement;
		}
		return null;
	}

	/**
	 * パララックス効果を設定
	 * @method cleatParallax
	 * @param {Element} target - 対象要素
	 * @param {Number} startY - 初期のY位置
	 * @param {Number} endY - 最終のY位置
	 * @param {Element} trigger - トリガー要素
	 */
	cleatParallax(target, startY, endY, trigger) {
		gsap.fromTo(
			target,
			{
				y: startY,
			},
			{
				y: endY,
				scrollTrigger: {
					trigger: trigger,
					start: 'top bottom',
					end: 'bottom top',
					scrub: 0,
				},
			},
		);
	}
}

/**
 * 要素をピン留めする機能を提供するクラス
 * @class ScrollPin
 */
class ScrollPin {
	/**
	 * @constructor
	 * @param {Number} breakpoints - ブレークポイント
	 */
	constructor(breakpoints) {
		/**
		 * ブレークポイントの幅
		 * @type {Number}
		 */
		this.breakpoints = breakpoints;

		/**
		 * ピン留めする要素のリスト
		 * @type {Array<Element>}
		 */
		this.pinElements = gsap.utils.toArray('[data-pin]');

		if (this.pinElements.length > 0) {
			this.init();
		}
	}

	/**
	 * 初期化
	 * @method init
	 */
	init() {
		this.pinElements.forEach((element) => {
			if (window.innerWidth > this.breakpoints) {
				const pinValue = element.dataset.pin;
				const [start, end, trigger, endTrigger] = pinValue
					.split(',')
					.map((value) => value.trim());
				this.clearPin(
					element,
					start,
					end,
					trigger === 'this' ? element : trigger,
					endTrigger,
				);
			} else {
				element.style.position = 'fixed';
			}
		});
	}

	/**
	 * 要素をピン留め設定
	 * @method clearPin
	 * @param {Element} element - ピン留めする要素
	 * @param {String} start - ピン留め開始位置
	 * @param {String} end - ピン留め終了位置
	 * @param {Element|String} trigger - トリガー要素またはセレクター
	 * @param {Element|String} endTrigger - 終了トリガー要素またはセレクター
	 */
	clearPin(element, start, end, trigger, endTrigger) {
		const triggerOptions = {
			trigger: trigger || element,
			pin: true,
			start: start || 'top top',
			endTrigger: endTrigger || 'body',
			end: end || 'bottom bottom',
			pinSpacing: false,
		};

		/**
		 * ScrollTrigger インスタンス
		 * @type {Object}
		 */
		const scrollTrigger = ScrollTrigger.create(triggerOptions);

		window.addEventListener('resize', () => {
			scrollTrigger.refresh();
		});
	}
}
